import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Shield } from "lucide-react";
import CaprocureLogo from "@/components/CaprocureLogo";
import { toast } from "sonner";
import PasswordChecklist from "@/components/PasswordChecklist";
import { validatePasswordClient } from "@/lib/password";

export default function Register() {
  const { register } = useAuth();
  const [sp] = useSearchParams();
  const initialRole = sp.get("role") === "supplier" ? "supplier" : "buyer";
  const [form, setForm] = useState({
    email: "", password: "", name: "", role: initialRole, company_name: "", phone: "", categories: [],
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => { setForm((f) => ({ ...f, role: initialRole })); }, [initialRole]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    // M-5: client-side gate mirrors the backend policy so the user gets
    // fast, clear feedback instead of a round-trip 400.
    const pwErr = validatePasswordClient(form.password);
    if (pwErr) { setErr(pwErr); return; }
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created");
      nav("/app");
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Card className="max-w-lg w-full p-10 border-slate-200">
        <Link to="/" className="mb-6 inline-block">
          <CaprocureLogo size="md" />
        </Link>
        <div className="eyebrow mb-2">Create account</div>
        <h2 className="font-display text-2xl font-bold text-[#0A2540]">Join the platform</h2>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label>I am a</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="register-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="buyer">Buyer — I need to purchase</SelectItem>
                <SelectItem value="supplier">Supplier — I want to quote</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="register-name-input" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="register-phone-input" />
            </div>
          </div>
          <div>
            <Label>Company</Label>
            <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} required data-testid="register-company-input" />
          </div>
          <div>
            <Label>Work email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="register-email-input" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={10} data-testid="register-password-input" />
            <PasswordChecklist password={form.password} />
          </div>
          {err && <div className="text-sm text-rose-600" data-testid="register-error">{err}</div>}
          <Button type="submit" disabled={loading} className="w-full bg-[#0A2540] hover:bg-[#061A2E]" data-testid="register-submit-btn">
            {loading ? "Creating…" : "Create account"}
          </Button>
          <div className="text-sm text-center text-slate-600">
            Have an account? <Link to="/login" className="text-[#1D4ED8] font-medium">Sign in</Link>
          </div>
        </form>
      </Card>
    </div>
  );
}
